import React from 'react';
import styled from 'styled-components';
import { useAppContext } from '../context/AppContext';
import { Container, SectionShell, Tag, radius, text } from '../styles/GlobalStyles';
import SectionHeader from './common/SectionHeader';
import Reveal from './common/Reveal';
import WordReveal from './common/WordReveal';

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 4fr);
  gap: 48px 88px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Prose = styled.div`
  max-width: 64ch;

  p {
    font-size: ${text.base};
    line-height: 1.7;
    color: ${props => props.theme.colors.textSecondary};
    text-wrap: pretty;
  }

  p + p {
    margin-top: 20px;
  }
`;

const Lead = styled(WordReveal)`
  margin-bottom: 96px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: 64px;
  }
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;

  img {
    width: 64px;
    height: 64px;
    border-radius: ${radius.lg};
    object-fit: cover;
  }

  strong {
    display: block;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
  }

  span {
    font-size: ${text.sm};
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  border-top: 1px solid ${props => props.theme.colors.border};

  dt,
  dd {
    padding: 12px 0;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    font-size: ${text.sm};
  }

  dt {
    padding-right: 24px;
    color: ${props => props.theme.colors.textTertiary};
  }

  dd {
    color: ${props => props.theme.colors.text};
    text-align: right;
  }
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px 56px;
  margin-top: 96px;
  padding-top: 32px;
  border-top: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 64px;
  }

  h3 {
    margin-bottom: 18px;
    font-size: ${text.sm};
    font-weight: 600;
    color: ${props => props.theme.colors.text};
  }
`;

const Entries = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Entry = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2px 16px;
  font-size: ${text.sm};

  strong {
    font-weight: 500;
    color: ${props => props.theme.colors.text};
  }

  span {
    grid-column: 1;
    color: ${props => props.theme.colors.textSecondary};
  }

  small {
    grid-row: 1;
    grid-column: 2;
    font-size: ${text.xs};
    font-variant-numeric: tabular-nums;
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const About: React.FC = () => {
  const { content } = useAppContext();
  const { about, personalInfo, education, languages, softSkills } = content;
  const [lead, ...rest] = about.description.split('\n\n');

  return (
    <SectionShell id="about">
      <Container>
        <SectionHeader index={1} title={about.title} />

        <Lead text={lead} />

        <Layout>
          <Reveal>
            <Prose>
              {rest.map(paragraph => (
                <p key={paragraph.slice(0, 24)}>{paragraph}</p>
              ))}
            </Prose>
          </Reveal>

          <Reveal delay={0.06}>
            <Profile>
              <img src={personalInfo.profileImage} alt={personalInfo.name} />
              <div>
                <strong>{personalInfo.name}</strong>
                <span>{personalInfo.location}</span>
              </div>
            </Profile>
            <Facts>
              {about.infoCards.map(card => (
                <React.Fragment key={card.title}>
                  <dt>{card.title}</dt>
                  <dd>{card.value}</dd>
                </React.Fragment>
              ))}
            </Facts>
          </Reveal>
        </Layout>

        <Reveal>
          <Details>
            <div>
              <h3>{education.title}</h3>
              <Entries>
                {education.items.map(item => (
                  <Entry key={item.institution}>
                    <strong>{item.major}</strong>
                    <small>{item.period}</small>
                    <span>
                      {item.degree}, {item.institution}
                    </span>
                  </Entry>
                ))}
              </Entries>
            </div>

            <div>
              <h3>{languages.title}</h3>
              <Entries>
                {languages.items.map(item => (
                  <Entry key={item.name}>
                    <strong>{item.name}</strong>
                    <small>{item.level}</small>
                  </Entry>
                ))}
              </Entries>
            </div>

            <div>
              <h3>{softSkills.title}</h3>
              <Tags>
                {softSkills.items.map(skill => (
                  <Tag key={skill}>{skill}</Tag>
                ))}
              </Tags>
            </div>
          </Details>
        </Reveal>
      </Container>
    </SectionShell>
  );
};

export default About;
